import { useQuery } from '@tanstack/react-query';

import { Card, DeadlinePill, Spinner } from '@axioma/ui';

import { PageHeader } from '../components/PageHeader.js';
import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

const COLUMNS = [
  { key: 'PENDING', label: 'Pendente' },
  { key: 'IN_PROGRESS', label: 'Em produção' },
  { key: 'IN_REVIEW', label: 'Em revisão' },
  { key: 'AWAITING_HUMAN', label: 'Aguardando humano' },
  { key: 'AWAITING_SIGNATURE', label: 'Aguardando assinatura' },
  { key: 'SIGNED', label: 'Assinada' },
  { key: 'PROTOCOLLED', label: 'Protocolada' },
  { key: 'ARCHIVED', label: 'Arquivada' },
] as const;

interface TaskRow {
  id: string;
  title: string;
  status: string;
  actionType: string;
  deadlineState: 'GREEN' | 'YELLOW' | 'ORANGE' | 'RED' | 'MISSED';
  legalDeadlineAt: string;
  process: { cnjNumberMasked: string };
}

export function Kanban(): JSX.Element {
  const tenant = useAuthStore((s) => s.currentTenantId);
  const query = useQuery({
    queryKey: ['vigilia', 'kanban', tenant],
    queryFn: async () => {
      const { data } = await api.get<{ items: TaskRow[] }>('/vigilia/tasks', {
        params: { pageSize: 200 },
      });
      return data;
    },
    enabled: Boolean(tenant),
  });

  const grouped = (query.data?.items ?? []).reduce<Record<string, TaskRow[]>>((acc, t) => {
    const list = acc[t.status] ?? [];
    list.push(t);
    acc[t.status] = list;
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        eyebrow="Vigília"
        title="Kanban de tarefas"
        description="Drag-and-drop entre colunas atualiza status em tempo real (multi-sessão)."
      />
      {query.isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" variant="aurora" />
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto px-6 py-5">
          <div className="flex gap-3 h-full min-w-max">
            {COLUMNS.map((col) => {
              const items = grouped[col.key] ?? [];
              return (
                <div
                  key={col.key}
                  className="w-72 flex-shrink-0 flex flex-col bg-ax-graphite/60 border border-ax-line rounded-ax-lg"
                >
                  <header className="px-3 py-2.5 border-b border-ax-line flex items-center justify-between">
                    <span className="ax-eyebrow text-ax-text-tertiary">{col.label}</span>
                    <span className="font-mono text-[11px] text-ax-text-secondary">
                      {String(items.length)}
                    </span>
                  </header>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {items.map((t) => (
                      <Card key={t.id} interactive className="p-3">
                        <div className="space-y-1.5">
                          <p className="font-display text-[13px] text-ax-text-primary leading-tight line-clamp-2">
                            {t.title}
                          </p>
                          <p className="font-mono text-[10px] text-ax-text-tertiary truncate">
                            {t.process.cnjNumberMasked}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <DeadlinePill
                              state={t.deadlineState}
                              label={new Date(t.legalDeadlineAt).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                              })}
                            />
                          </div>
                        </div>
                      </Card>
                    ))}
                    {items.length === 0 && (
                      <div className="text-center py-6 text-ax-text-tertiary text-xs font-mono uppercase tracking-wider">
                        Vazio
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
